import React, {FC, ChangeEvent} from "react";
import { createStyles, lighten, makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { CalendarResource } from "../../model/calendarResource";
import { TimeCell } from "../../model/timeCell";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Autocomplete, { AutocompleteChangeReason, AutocompleteChangeDetails } from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import { PropertyResource } from "../../model/propertyResource";
import PropertyService from "../../service/property-service";
import {
  createColumns,
  createRows,
  NONE_ID,
  LEDGEND_TYPE,
  TIME_COLUMN_ID,
  getTimeCellsByPropertyId,
  getTimeCellResourceById,
  getTimeColumn,
  getSelectedResources,
} from "../../utils/calendarUtils";
import moment, { Moment } from "moment-timezone";
import { CalendarRow } from "../../model/calendarRow";
import { CalendarColumn } from "../../model/calendarColumn";
import { Button, Grid } from "@material-ui/core";
import ChevronLeftOutlined from "@material-ui/icons/ChevronLeftOutlined";
import ChevronRightOutlined from "@material-ui/icons/ChevronRightOutlined";
import ResourceGroup from "./resource-group";
import d from "debug";
import {
  GUIDED_TOUR, SELF_GUIDED_TOUR, VIRTUAL_TOUR
} from "../constants";


const debug = d("TourCalendar");

const DISPLAY_MONTH_FORMAT = "MMMM";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      minWidth: 1000,
      margin: 10,
      padding: "10px 10px 30px 10px",
    },
    container: {
      maxHeight: 700,
    },
    formControl: {
      width: "100%",
      minWidth: 80
    },
    formInput: {
      width: "100%",
      backgroundColor: "white",
      minWidth: 80
    },
    secondaryButton: {
      color: "#CC3600",
      borderRadius: "0px",
      backgroundColor: "white",
      "&:hover": {
        backgroundColor: "rgba(239,78,34, 0.20)",
      },
      marginRight: theme.spacing(1),
    },
    table: {
      fontSize: "12px",
      minWidth: 1500,
      maxHeight: 500,
    },
    tbody: {
      display: "block" /* to enable vertical scrolling */,
      maxHeight: "200px",
      overflowY: "auto",
    },
    tableHead: {
      backgroundColor: "#e8e8e8",
    },
    link: {
      fontSize: 12,
      color: "#404040",
    },
    headCell: {
      fontSize: "12px",
      fontWeight: "bold",
    },
    tableCell: {
      "& .MuiFormControlLabel-root": {
        height: "18px",
        fontSize: "12px",
      },
      "& .MuiFormGroup-root": {
        height: "18px",
        fontSize: "12px",
      },
      fontSize: "12px",
      minWidth: 80,
    },
    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1,
    },
    title: {
      flex: "1 1 100%",
    },
    evenRow: {
      backgroundColor: "inherit",
    },
    oddRow: {
      backgroundColor: "#e8e8e8",
    },
    MuiTableRow: {
      height: "18px",
      "& .MuiTableCell-root": {
        padding: 2,
        height: "18px",
        fontSize: "12px",
      },
    },
    MuiFormControlLabel: {
      height: "18px",
    },
    MuiTablePaginationInput: {
      paddingLeft: "4px",
    },
    selectRoot: {
      fontSize: 12,
    },
    selectIcon: {
      right: 1,
    },
    highlight:
      theme.palette.type === "light"
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
          },
  })
);

const applyResourceSelections = (rows: CalendarRow[], selectionMap: Map<string, string>) => {
  if (rows) {
    rows.forEach((r) => {
      if (r.timeCells) {
        r.timeCells.forEach((cell) => {
          const selectedId = selectionMap.get(cell.id);
          if (selectedId) {
            cell.selectedId = selectedId;
            cell.selectedResource = getTimeCellResourceById(cell, selectedId,'Tour');
          }
        });
      }
    });
  }
};

const createResourceSelections = (rows: CalendarRow[]): Map<string, string> => {
  const selections = new Map<string, string>();
  // Create Selections
  if (rows) {
    rows.forEach((r) => {
      if (r.timeCells) {
        r.timeCells.forEach((cell) => {
          selections.set(cell.id, cell.selectedId || NONE_ID);
        });
      }
    });
  }
  return selections;
};

interface TourCalendarProps {
  numberOfWeeks: number;
  startDayOfWeek: number;
  startHour: number;
  endHour: number;
  numberDisplayDays: number;
  viewDate: Moment;
  calendarResources?: CalendarResource[];
  selectedResources: Map<string, string>;
  resourceSelectionsChange: (selectionsMap: Map<string, string>, selectedResources: CalendarResource[]) => void;
  allPropertyResources: PropertyResource[];
  propertySelected: (selectedProperty?: PropertyResource) => void;
  allCities: PropertyResource[];
  refPropertyResource : PropertyResource;
  selfGuidedTourTag: string;
  selfGuidedTourName: string;
}

export const TourCalendar: FC<TourCalendarProps> = ({
  calendarResources,
  selectedResources,
  numberOfWeeks,
  startDayOfWeek,
  startHour,
  endHour,
  numberDisplayDays,
  viewDate,
  resourceSelectionsChange,
  allPropertyResources,
  propertySelected,
  allCities,
  refPropertyResource,
  selfGuidedTourTag,
  selfGuidedTourName
}) => {
  const classes = useStyles(useTheme());
  const [columns, setColumns] = React.useState<CalendarColumn[]>([]);
  const [rows, setRows] = React.useState<CalendarRow[]>([]);
  const [displayColumns, setDisplayColumns] = React.useState<CalendarColumn[]>([]);
  const [displayIndex, setDisplayIndex] = React.useState(0);
  const [displayStartDate, setDisplayStartDate] = React.useState<Moment>();
  const [displayEndDate, setDisplayEndDate] = React.useState<Moment>();
  const [displayMonth, setDisplayMonth] = React.useState(viewDate.format(DISPLAY_MONTH_FORMAT));
  const [resourceSelections, setResourceSelections] = React.useState<Map<string, string>>(selectedResources);
  const [tourType, setTourType] =  React.useState<string>(GUIDED_TOUR);
  const [selectedProperty, setSelectedProperty] = React.useState<PropertyResource | null>();
  const [selectedCity, setSelectedCity] = React.useState<PropertyResource | null>();
  React.useEffect(() => {
     applyResourceSelections(rows, selectedResources);
  }, []);

  React.useEffect(() => {
    const newRows = createRows(calendarResources, columns, startHour, endHour);
    setRows(newRows);
  }, [calendarResources, columns, endHour, startHour]);

  React.useEffect(() => {
    const newColumns = createColumns(viewDate, startDayOfWeek, numberOfWeeks * 7);
    setColumns(newColumns);
    setDisplayColumns(newColumns);
  }, [numberOfWeeks, startDayOfWeek, viewDate]);

  React.useEffect(() => {
    // Get columns for specific Date range
    if (!columns || columns.length < 2) {
      return;
    }
    const newColumns: CalendarColumn[] = [];
    const timeColumn = getTimeColumn(columns);
    if (timeColumn) {
      newColumns.push(timeColumn);
    }
    const start = displayIndex + 1; // skip time column @ postiton zero
    const end = start + numberDisplayDays;
    for (let i = start; i < end && i < columns.length; i++) {
      // skip time column @ postiton zero
      newColumns.push(columns[i]);
    }

    // Set the columns and rows
    const startDate = moment(newColumns[1].columnDate);
    const endDate = moment(newColumns[newColumns.length - 1].columnDate);
    startDate.hour(0);
    startDate.minute(0);
    startDate.second(0);
    startDate.millisecond(0);
    endDate.hour(23);
    endDate.minute(59);
    endDate.second(59);
    endDate.millisecond(0);

    setDisplayStartDate(startDate);
    setDisplayEndDate(endDate);
    setDisplayColumns(newColumns);
    const startMonth = startDate.format(DISPLAY_MONTH_FORMAT);
    const endMonth = endDate.format(DISPLAY_MONTH_FORMAT);
    const year = endDate.format("YYYY");
    setDisplayMonth(startMonth === endMonth ? `${startMonth} ${year}` : `${startMonth} / ${endMonth} ${year}`);
  }, [columns, displayIndex, numberDisplayDays]);

  const handleLeftNav = (event: any) => {
    const newDisplayIndex = displayIndex - numberDisplayDays;
    if (newDisplayIndex >= 0) {
      setDisplayIndex(newDisplayIndex);
    }
  };

  const handleRightNav = (event: any) => {
    const newDisplayIndex = displayIndex + numberDisplayDays;
    if (newDisplayIndex < columns.length) {
      setDisplayIndex(newDisplayIndex);
    }
  };

  /**
   * Only one selection of a specific property resource time slot can be selected.
   * Ensure all timeslots for the selected resource are unchecked except the one passed in.
   * Deselect all within the Displayed Resource Groups and also those not currently displayed.
   * @param timeCell the newly selected property resource
   */
  const handleChangeResourceGroupChanged = (timeCell: TimeCell) => {
    if (timeCell.selectedResource) {
      // Remove any duplicate selections in master resource list to ensure only one selection for propertyId
      const propertyTimeCells = getTimeCellsByPropertyId(calendarResources, rows, timeCell, true);
      propertyTimeCells.forEach((cell) => {
        // Clear any property cells with a duplicate selected ID
        if (cell.selectedResource && 
            cell.selectedResource.propertyId === timeCell.selectedResource?.propertyId) {
          cell.selectedId = NONE_ID;
          cell.selectedResource = undefined;
        }
      });
    }
    const selections = createResourceSelections(rows);
    setResourceSelections(selections);

    const selectedResourceList = getSelectedResources(rows);
    resourceSelectionsChange(selections, selectedResourceList);
  };

  const handleTourTypeChange = e => {
    setTourType(e.target.value);
    console.log('ReSetting tour type '+e.target.value);
    setResourceSelections(new Map<string, string>());
  };

  const handleCityNameChange = (
    event: ChangeEvent<{}>,
    value: PropertyResource | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<PropertyResource>
  ) => {
    setSelectedCity(value);
  };

  const handleSelectionChanged = (
    event: ChangeEvent<{}>,
    value: PropertyResource | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<PropertyResource>
  ) => {
    setSelectedProperty(value);
    propertySelected(value);
  };

  return (
    <Grid container item xs={12} className={classes.root} direction="column" justify="flex-start" alignItems="stretch" spacing={1}>
      <Grid container spacing={3} direction="row" justify="space-between" >
        <Grid item md={3} lg={4}>
            <FormControl variant="outlined" className={classes.formControl}>
          <InputLabel id="tourTypeLabel"> Tour Type *</InputLabel>
          <Select
            variant="outlined"
            autoWidth
            className={classes.formInput}
            labelId="tourTypeLabel"
            label="City *"
            onChange={handleTourTypeChange}
            id="tourType"
            value={tourType}>
            <MenuItem key={0} value={GUIDED_TOUR}>Guided Tour</MenuItem>
            <MenuItem key={1} value={VIRTUAL_TOUR}>Guided Virtual Tour</MenuItem>
            <MenuItem key={2} value={selfGuidedTourTag}>{selfGuidedTourName}</MenuItem>
          </Select>
        </FormControl>
        </Grid>
        <Grid item md={3} lg={4}>
        <FormControl variant="outlined" className={classes.formControl}>
          <Autocomplete
          id="combo-box-demo"
          options={allCities}
          getOptionLabel={(option) => option.name}
          style={{ minWidth: 200 }}
          onChange={handleCityNameChange}
          renderInput={(params) => <TextField {...params} label="City Name" variant="outlined" />}
        />
        </FormControl>
        </Grid>
        <Grid item md={3} lg={4}>
            <FormControl variant="outlined" className={classes.formControl}>
          <Autocomplete
          id="combo-box-demo"
          options={selectedCity == null ? allPropertyResources: allPropertyResources.filter((r)=> selectedCity != null && r.accountParentName === selectedCity.name)}
          getOptionLabel={(option) => option.name}
          style={{ minWidth: 200 }}
          onChange={handleSelectionChanged}
          renderInput={(params) => <TextField {...params} label="Neighborhood Name" variant="outlined" />}
        />
        </FormControl>
        </Grid>
        <Grid item md={3}></Grid>
      </Grid>
      <Grid item xs={12} container direction="row" justify="space-between" alignItems="center" spacing={2}>
        <Grid item>
          <Button
            className={classes.secondaryButton}
            variant="outlined"
            color="primary"
            disabled={displayIndex < 1}
            onClick={handleLeftNav}
          >
            <ChevronLeftOutlined />
          </Button>
        </Grid>
        <Grid item>
          <Typography variant="h6">{displayMonth}</Typography>
        </Grid>
        <Grid item>
          <Button
            className={classes.secondaryButton}
            variant="outlined"
            color="primary"
            disabled={displayIndex + numberDisplayDays >= columns.length - 1}
            onClick={handleRightNav}
          >
            <ChevronRightOutlined />
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <TableContainer className={classes.container}>
          <Table stickyHeader size="small" aria-label="sticky table">
            <TableHead>
              <TableRow>
                {displayColumns.map((column) => (
                  <TableCell key={column.id} align="center">
                    {column.id === TIME_COLUMN_ID ? (
                      <div>&nbsp;</div>
                    ) : (
                      <div>
                        <Typography variant="body1">
                          {column.columnDate.format("ddd")}
                          <br />
                        </Typography>
                        <Typography variant="body1">{column.columnDate.format("D")}</Typography>
                      </div>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                return (
                  <TableRow className={classes.MuiTableRow} hover role="checkbox" tabIndex={-1} key={row.id}>
                    {row.timeCells.map((cell) => {
                      if (cell.cellType === LEDGEND_TYPE) {
                        return (
                          <TableCell key={cell.id} align="center" valign="top">
                            <Typography variant="body1">{row.id.toUpperCase()}
                            {moment.tz('America/Los_Angeles').format(" z")}
                            </Typography>
                          </TableCell>
                        );
                      } else if (cell.startTime.isSameOrAfter(displayStartDate) && cell.endTime.isBefore(displayEndDate)) {
                        return (
                          <TableCell key={cell.id} align="left">
                            <ResourceGroup
                              key={cell.id}
                              cell={cell}
                              selectedId={resourceSelections?.get(cell.id)}
                              maxDisplayRows={10}
                              resourceGroupChanged={handleChangeResourceGroupChanged}
                              tourType={tourType}
                            />
                          </TableCell>
                        );
                      } else {
                        return null;
                      }
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};
